import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { askFaq, escalateFaq, openFaqSuggestion, rateFaq, type FaqAnswer } from '../api/faqApi';

/** SRS #19 — FAQ support assistant: answers from the knowledge base, offers
 *  suggestions when unsure, and escalates to a ticket when it cannot help. */

type Msg =
  | { role: 'user'; text: string }
  | { role: 'bot'; answer: FaqAnswer; rated?: boolean; ticketId?: number };

const STARTERS = ['How do I set up an alert?', 'How do I change my newsletter frequency?', 'How do I cancel my subscription?', 'Which jurisdictions are covered?'];

export default function FaqChat({ canEscalate, onClose }: { canEscalate: boolean; onClose?: () => void }) {
  const [msgs, setMsgs] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView?.(); }, [msgs]);

  const ask = async (q: string) => {
    const question = q.trim();
    if (!question || busy) return;
    setInput(''); setError(null); setBusy(true);
    setMsgs(m => [...m, { role: 'user', text: question }]);
    try {
      const a = await askFaq(question);
      setMsgs(m => [...m, { role: 'bot', answer: a }]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'The assistant is unavailable right now.');
    } finally { setBusy(false); }
  };
  const pick = async (idx: number, conversationId: number, faqId: number) => {
    try {
      const a = await openFaqSuggestion(conversationId, faqId);
      setMsgs(m => m.map((x, i) => (i === idx ? { role: 'bot', answer: a } : x)));
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not open that answer.'); }
  };
  const rateMsg = async (idx: number, conversationId: number, helpful: boolean) => {
    try {
      await rateFaq(conversationId, helpful);
      setMsgs(m => m.map((x, i) => (i === idx && x.role === 'bot' ? { ...x, rated: true, answer: { ...x.answer, can_escalate: x.answer.can_escalate && !helpful } } : x)));
    } catch { /* rating is best-effort */ }
  };
  const escalateMsg = async (idx: number, conversationId: number) => {
    try {
      const r = await escalateFaq(conversationId);
      setMsgs(m => m.map((x, i) => (i === idx && x.role === 'bot' ? { ...x, ticketId: r.ticket_id } : x)));
    } catch (e) { setError(e instanceof Error ? e.message : 'Could not create a ticket.'); }
  };

  const bubble = (mine: boolean): React.CSSProperties => ({
    maxWidth: '88%', padding: '9px 12px', borderRadius: 12, fontSize: 13.5, lineHeight: 1.55, whiteSpace: 'pre-wrap',
    alignSelf: mine ? 'flex-end' : 'flex-start',
    background: mine ? 'var(--ds-brand-blue)' : 'var(--color-bg-subtle, var(--ds-surface-2))', color: mine ? 'var(--ds-text-on-dark)' : 'var(--color-text, var(--ds-text))',
  });
  const small: React.CSSProperties = { padding: '4px 10px', borderRadius: 999, border: '1px solid var(--color-border-subtle, var(--ds-border-strong))', background: 'transparent', cursor: 'pointer', fontSize: 12, color: 'var(--color-text, var(--ds-text))' };

  return (
    <div data-testid="faq-chat" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div role="log" aria-live="polite" style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 340, overflowY: 'auto', padding: 2 }}>
        {msgs.length === 0 && (
          <div style={{ fontSize: 13, color: 'var(--color-text-muted, var(--ds-text-muted))' }}>
            Ask anything about using PolicyOra — alerts, plans, coverage, exports, teams. Try:
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
              {STARTERS.map(s => <button key={s} type="button" style={small} onClick={() => ask(s)}>{s}</button>)}
            </div>
          </div>
        )}
        {msgs.map((m, i) => m.role === 'user' ? (
          <div key={i} style={bubble(true)}>{m.text}</div>
        ) : (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start', maxWidth: '100%' }}>
            <div style={bubble(false)}>
              {m.answer.kind === 'faq' && <div style={{ fontWeight: 700, marginBottom: 4 }}>{m.answer.question}</div>}
              {m.answer.answer}
              {m.answer.kind === 'faq' && m.answer.link && (
                <div style={{ marginTop: 6 }}>
                  {m.answer.link.startsWith('http')
                    ? <a href={m.answer.link} target="_blank" rel="noreferrer">Open →</a>
                    : <Link to={m.answer.link} onClick={onClose}>Open in app →</Link>}
                </div>
              )}
            </div>
            {m.answer.suggestions.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {m.answer.suggestions.map(s => <button key={s.id} type="button" style={small} onClick={() => pick(i, m.answer.conversation_id, s.id)}>{s.question}</button>)}
              </div>
            )}
            {m.ticketId ? (
              <div style={{ fontSize: 12.5, color: 'var(--ds-success-text)' }}>Ticket #{m.ticketId} opened — the team replies by email and under <Link to="/support" onClick={onClose}>Support</Link>.</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', fontSize: 12, color: 'var(--color-text-muted, var(--ds-text-muted))' }}>
                {m.answer.kind === 'faq' && !m.rated && (<>
                  Helpful?
                  <button type="button" style={small} aria-label="Yes, helpful" onClick={() => rateMsg(i, m.answer.conversation_id, true)}>👍</button>
                  <button type="button" style={small} aria-label="No, not helpful" onClick={() => rateMsg(i, m.answer.conversation_id, false)}>👎</button>
                </>)}
                {m.rated && !m.answer.can_escalate && <span>Thanks for the feedback.</span>}
                {m.answer.can_escalate && canEscalate && (m.answer.kind !== 'faq' || m.rated) && (
                  <button type="button" style={{ ...small, borderColor: 'var(--ds-brand-blue)', color: 'var(--ds-link)' }} onClick={() => escalateMsg(i, m.answer.conversation_id)}>Send to support</button>
                )}
                {m.answer.kind !== 'faq' && !canEscalate && <span>Sign in to send this to the support team.</span>}
              </div>
            )}
          </div>
        ))}
        {busy && <div style={{ ...bubble(false), color: 'var(--color-text-muted, var(--ds-text-muted))' }}>Looking that up…</div>}
        <div ref={endRef} />
      </div>
      {error && <div style={{ fontSize: 12.5, color: 'var(--ds-danger-text)' }}>{error}</div>}
      <form onSubmit={e => { e.preventDefault(); ask(input); }} style={{ display: 'flex', gap: 6 }}>
        <input
          value={input} onChange={e => setInput(e.target.value)} placeholder="Ask a question…" aria-label="Ask a question" maxLength={1000}
          style={{ flex: 1, padding: '9px 12px', borderRadius: 8, border: '1.5px solid var(--color-border-subtle, var(--ds-border))', fontSize: 14, outline: 'none', background: 'var(--color-input-bg, #fff)', color: 'var(--color-text, var(--ds-text))' }}
        />
        <button type="submit" disabled={busy || !input.trim()} style={{ padding: '9px 16px', borderRadius: 8, border: 'none', background: 'var(--ds-brand-blue)', color: 'var(--ds-text-on-dark)', fontWeight: 700, cursor: 'pointer', opacity: busy || !input.trim() ? 0.6 : 1 }}>Ask</button>
      </form>
    </div>
  );
}
